import queryString from 'query-string';

import {
  PaginationInfo,
  PageableAPIResponse,
  ListAPIResponse,
} from 'types/frontend';

/**
 * @description paginateApiResponse is a helper function to faciliate
 * pagination based on links in an API response in the OUI
 * PaginationControls component.
 *
 * This hook is meant to be used by API specific HOCs in services/api,
 * to wrap responses fetched from the API, rather than to be used
 * directly in any UI components.
 */
export default function paginateApiResponse<T extends ListAPIResponse>(
  apiResponse: T,
  fetchData: (args: object) => void
): PageableAPIResponse<T> {
  function parseUrl(pagetoFetch: string) {
    const paramString = pagetoFetch.substring(pagetoFetch.indexOf('?') + 1);
    const params = queryString.parse(paramString);
    return fetchData(params);
  }

  function goToPage(pageNumber: number) {
    if (pageNumber === apiResponse.total_pages) {
      return parseUrl(apiResponse.last_url);
    }

    if (pageNumber === 1) {
      return parseUrl(apiResponse.first_url);
    }

    const isPreviousPage = pageNumber < apiResponse.page;
    const newPageIndex = Math.abs(pageNumber - apiResponse.page) - 1;
    if (isPreviousPage && apiResponse.prev_url) {
      let prevUrl = Array.isArray(apiResponse.prev_url)
        ? apiResponse.prev_url
        : [apiResponse.prev_url];
      prevUrl = prevUrl.reverse();
      return parseUrl(prevUrl[newPageIndex]);
    } else if (apiResponse.next_url) {
      const nextUrl = Array.isArray(apiResponse.next_url)
        ? apiResponse.next_url
        : [apiResponse.next_url];
      return parseUrl(nextUrl[newPageIndex]);
    }
    return Promise.resolve();
  }

  let totalSlots = 1;
  if (apiResponse.prev_url) {
    totalSlots += Array.isArray(apiResponse.prev_url)
      ? apiResponse.prev_url.length
      : 1;
  }
  if (apiResponse.next_url) {
    totalSlots += Array.isArray(apiResponse.next_url)
      ? apiResponse.next_url.length
      : 1;
  }

  const {
    page,
    total_pages,
    next_url,
    last_url,
    first_url,
    prev_url,
    ...apiResponseFieldsToInclude
  } = apiResponse;

  const paginationInfo: PaginationInfo['pagination_info'] = {
    current_page: apiResponse.page,
    go_to_page: goToPage,
    page_slots: totalSlots,
    total_pages: apiResponse.total_pages,
  };

  return {
    ...apiResponseFieldsToInclude,
    pagination_info: paginationInfo,
  };
}
